import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Company, LoginCredentials, AuthResponse, CompanyRegistration } from '../types';
import { authAPI } from '../services/api';

interface AuthContextType {
  user: User | null;
  company: Company | null;
  token: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  registerCompany: (data: CompanyRegistration) => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  isRole: (role: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedCompany = localStorage.getItem('company');

      if (storedToken && storedUser && storedCompany) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          setCompany(JSON.parse(storedCompany));
          
          // Verify token is still valid
          const currentUserData = await authAPI.getCurrentUserWithCompany();
          setUser(currentUserData.user);
          setCompany(currentUserData.company);
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          setToken(null);
          setUser(null);
          setCompany(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const response: AuthResponse = await authAPI.login(credentials);
      
      setToken(response.token);
      setUser(response.user);
      setCompany(response.company);
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('company', JSON.stringify(response.company));
    } catch (error) {
      throw error;
    }
  };

  const registerCompany = async (data: CompanyRegistration) => {
    try {
      const response: AuthResponse = await authAPI.registerCompany(data);
      
      setToken(response.token);
      setUser(response.user);
      setCompany(response.company);
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('company', JSON.stringify(response.company));
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      setCompany(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
    }
  };

  const hasPermission = (permission: string): boolean => {
    return user?.permissions.includes(permission) || false;
  };

  const isRole = (role: string | string[]): boolean => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };

  const value: AuthContextType = {
    user,
    company,
    token,
    login,
    logout,
    registerCompany,
    loading,
    isAuthenticated: !!token && !!user,
    hasPermission,
    isRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};